import classNames from "classnames";
import type Flickity from "flickity";
import { Immutable } from "immer";
import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { safeDestroy } from "../../hooks/useFlickity";

export interface ICardSlider {
    imageUrl: string;
    copy: string;
}

// Not using useFlickity hook: requires resize-based conditional init/destroy
type FlickityConstructor = new (el: HTMLElement, options: object) => Flickity;

const initFlickity = (
    FlickityClass: FlickityConstructor,
    carousel: HTMLUListElement,
): Flickity => {
    return new FlickityClass(carousel, {
        cellAlign: "left",
        wrapAround: true,
        pageDots: true,
        imagesLoaded: true,
        arrowShape: {
            x0: 15,
            x1: 100,
            y1: 110,
            x2: 105,
            y2: 110,
            x3: 20,
        },
    });
};

const shouldUseCarousel = (
    carousel: HTMLUListElement,
    isWindowMobile: boolean,
): boolean => {
    const carouselLength = carousel.querySelectorAll(
        ".card-slider__column",
    ).length;
    return carouselLength >= 4 || (carouselLength >= 2 && isWindowMobile);
};

const buildFullImages = (
    images: readonly ICardSlider[],
    isNormalLayout: boolean,
): React.JSX.Element[] => {
    const classes = classNames({
        "card-slider__column": true,
        "card-slider__column--normal-layout": isNormalLayout && true,
    });

    return images.map((img, i) => {
        const fullStyle: React.CSSProperties = {
            backgroundImage: `url(${img.imageUrl})`,
        };
        return (
            <li key={i} className={classes}>
                <div className="card-slider__image" style={fullStyle}></div>
                <div className="card-slider__copy-container">
                    <div
                        className="card-slider__copy"
                        dangerouslySetInnerHTML={{ __html: img.copy }}
                    ></div>
                </div>
            </li>
        );
    });
};

export const CardSlider = (
    props: Immutable<{
        images: ICardSlider[];
    }>,
): React.ReactNode => {
    const [isNormalLayout, setIsNormalLayout] = React.useState(false);
    const carouselRef = React.useRef<HTMLUListElement>(null);
    const flickityRef = React.useRef<Flickity | null>(null);

    React.useEffect(() => {
        const window = getWindow();
        const carouselElem = carouselRef.current;
        if (!carouselElem || !window) {
            return;
        }

        let resizeHandler: (() => void) | null = null;

        // Skip on SSR - flickity requires window
        if (import.meta.env.SSR) {
            return;
        }

        import("flickity-imagesloaded").then((module) => {
            const FlickityClass =
                module.default as unknown as FlickityConstructor;

            const recalcFlickityDisplay = (): void => {
                if (!window) return;
                const isWindowMobile = window.innerWidth < 501;
                const useCarousel = shouldUseCarousel(
                    carouselElem,
                    isWindowMobile,
                );

                if (useCarousel) {
                    setIsNormalLayout(false);
                    if (flickityRef.current) {
                        safeDestroy(flickityRef.current);
                    }
                    flickityRef.current = initFlickity(
                        FlickityClass,
                        carouselElem,
                    );
                } else {
                    if (flickityRef.current) {
                        safeDestroy(flickityRef.current);
                        flickityRef.current = null;
                    }
                    setIsNormalLayout(true);
                }
            };

            recalcFlickityDisplay();
            resizeHandler = recalcFlickityDisplay;
            window.addEventListener("resize", recalcFlickityDisplay);
        });

        return () => {
            if (resizeHandler) {
                window.removeEventListener("resize", resizeHandler);
            }
            if (flickityRef.current) {
                safeDestroy(flickityRef.current);
                flickityRef.current = null;
            }
        };
    }, []);

    const classes = classNames({
        "card-slider__columns": true,
        "card-slider__columns--normal-layout": isNormalLayout && true,
    });

    return (
        <div className="card-slider__carousel-content">
            <ul className={classes} ref={carouselRef}>
                {buildFullImages(props.images, isNormalLayout)}
            </ul>
        </div>
    );
};
